import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import { catchError, map } from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {
  Merchant,
  WorkTime, Group, DailyAvailabilityResponse, Restaurant
} from '../models/restaurant.model';
import {RouteService} from './route.service';
import {
  dataToGroup,
  dataToRestaurant,
} from '../dto/restaurant.dto';
import { FulfillmentTypeItem } from 'src/app/pages/checkout/models/location.model';
import { FulfillmentType } from '../models/order.model';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {OrderOnlyModalComponent} from '../../shared/components/order-only-modal/order-only-modal.component';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import { Category, Item, Variation } from '../models/catalog.model';
import { DailyAvailability, dataToCategory } from '../dto/catalog.dto';
import { BehaviorSubject, throwError } from 'rxjs';

@Injectable()
export class RestaurantService {
  private noCacheHttpOptions = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Content-Type':  'application/json'
    })
  };
  public _cloverToken: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public readonly fulfillmentTypeItems: { [key in FulfillmentType]: FulfillmentTypeItem } = {
    [FulfillmentType.PICKUP]: new FulfillmentTypeItem('Pickup', 'icon-shoppingbag', true, FulfillmentType.PICKUP, 0),
    [FulfillmentType.CURBSIDE]: new FulfillmentTypeItem('Curbside', 'icon-curbside', true, FulfillmentType.CURBSIDE, 0),
    [FulfillmentType.TABLESIDE]: new FulfillmentTypeItem('Tableside', 'icon-tableside', true, FulfillmentType.TABLESIDE, 0),
    [FulfillmentType.DELIVERY]: new FulfillmentTypeItem('Delivery', 'icon-moped', true, FulfillmentType.DELIVERY, 0),
    [FulfillmentType.HANDOFF]: new FulfillmentTypeItem('X-Side', 'icon-pickup', true, FulfillmentType.HANDOFF, 0),
    [FulfillmentType.TAB]: new FulfillmentTypeItem('Tab', 'icon-card', true, FulfillmentType.TAB, 0),
    [FulfillmentType.FRIDGE]: new FulfillmentTypeItem('Fridge', 'icon-fridge', true, FulfillmentType.FRIDGE, 0),
    [FulfillmentType.GIFTCARD]: new FulfillmentTypeItem('GiftCard', 'icon-gift-card', true, FulfillmentType.GIFTCARD, 0)
  };

  public getFulfillmentTypeItem(fulfillment: FulfillmentType): FulfillmentTypeItem {
    return this.fulfillmentTypeItems[fulfillment];
  }

  constructor(
    private http: HttpClient,
    private routeService: RouteService,
    private router: Router,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver
  ) {
  }

  public getMerchantById(merchantId: string): Observable<Merchant> {
    const apiURL = environment.apiUrl;
    return this.http.get<Merchant>(`${apiURL}merchants/${merchantId}`);
  }

  public getGroup(baseUrl: string): Observable<Group> {
    const apiURL = environment.apiUrl + baseUrl;
    return this.http.get(`${apiURL}`)
      .pipe(map( (response: any) => dataToGroup(response)));
  }

  public getCloverToken(merchantId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}merchants/${merchantId}/pakms/apikey`).pipe(
      map((response: any) => {
        this._cloverToken.next(response.api_access_key);
        return response.api_access_key;
      })
    );
  }

  public getRestaurants(baseUrl): Observable<any> {
    const apiURL = environment.apiUrl + baseUrl;
    return this.http.get(`${apiURL}/venues/active`)
      .pipe(
        map((restaurantsResponse: any): any => {
          return restaurantsResponse.map((restaurant) => {
            if (restaurant?.kyoo_location?.contact_phone?.includes('000000')) {
              restaurant.kyoo_location.contact_phone = null;
            }
            return dataToRestaurant(restaurant);
          });
        }));
  }

  public getFullCatalog(restaurant: Restaurant, pickupAt?: string | null): Observable<any> {
    const base = `${environment.catalogApiUrl}merchants/${restaurant.merchantId}/locations/${restaurant.locationId}`;
    const primaryUrl = `${base}/all-catalog.json?${this.faker(5)}`;
    const fallbackUrl = `${environment.apiUrl}locations/${restaurant.locationId}/catalog?${this.faker(5)}`;

    return this.http.get<any[]>(primaryUrl).pipe(
      map(res => this.normalizeCatalog(res, restaurant, pickupAt)),
      catchError(primaryErr => {
        return this.http.get<any[]>(fallbackUrl).pipe(
          map(res => this.normalizeCatalog(res, restaurant, pickupAt)),
          catchError(fallbackErr => {
            return throwError(() => new Error(
              `Failed to load catalog: primary (${primaryUrl}) and fallback (${fallbackUrl})`
            ));
          })
        );
      })
    );
  }

  private normalizeCatalog(response: any[] | null | undefined, restaurant: Restaurant, pickupAt?: string | null): Category[] {
    const unavailableItems: Item[] = [];

    let categories = (response ?? [])
      .map(category => dataToCategory(category, restaurant, unavailableItems, pickupAt))
      .filter((c: Category | null): c is Category => c !== null);

    if (restaurant.showNotCurrentlySoldCategory && unavailableItems.length > 0) {
      categories.push(
        new Category(
          'NOT_CURRENTLY_SOLD',
          'Not Currently Sold',
          '../../../../../assets/images/no-image.svg',
          unavailableItems,
          [],
          restaurant.categoriesCollapsable,
          restaurant.categoriesCollapsable
        ),
      );
    }

    categories = this.applySoldOutPolicy(categories, restaurant);

    return categories;
  }

  private applySoldOutPolicy(categories: Category[], restaurant: Restaurant): Category[] {
    if (restaurant.hideSoldOutItems) {
      return this.filterOutSoldOut(categories, restaurant);
    }
    if (restaurant.showSoldOutItemsLast) {
      return this.sortSoldOutItemsLast(categories, restaurant);
    }
    return categories;
  }

  private filterOutSoldOut(categories: Category[], restaurant: Restaurant): Category[] {
    return categories
      .map(c => {
        const filteredSub = c.subcategories?.length
          ? this.filterOutSoldOut(c.subcategories, restaurant)
          : [];

        const filteredItems = (c.items ?? []).filter(item => !this.isSoldOut(item));

        if (filteredItems.length === 0 && filteredSub.length === 0) {
          return null;
        }

        return new Category(
          c.id,
          c.name,
          c.image_url,
          filteredItems,
          filteredSub,
          c.isCollapse,
          c.hideItems,
          c.showSubCategories,
          c.parentCategoryId
        );
      })
      .filter((c: Category | null): c is Category => c !== null);
  }

  private sortSoldOutItemsLast(categories: Category[], restaurant: Restaurant): Category[] {
    if (!Array.isArray(categories)) return categories;

    return categories.map(c => {
      const sub = c.subcategories?.length
        ? this.sortSoldOutItemsLast(c.subcategories, restaurant)
        : c.subcategories;

      if (!c.items?.length) {
        return new Category(
          c.id,
          c.name,
          c.image_url,
          c.items,
          sub,
          c.isCollapse,
          c.hideItems,
          c.showSubCategories,
          c.parentCategoryId
        );
      }

      const available: Item[] = [];
      const soldOut: Item[] = [];
      for (const item of c.items) {
        (this.isSoldOut(item) ? soldOut : available).push(item);
      }

      return new Category(
        c.id,
        c.name,
        c.image_url,
        available.concat(soldOut),
        sub,
        c.isCollapse,
        c.hideItems,
        c.showSubCategories,
        c.parentCategoryId
      );
    });
  }


  private isSoldOut(product: Item): boolean {
    return (this.isProductSold(product) && this.getQuantity(product) <= 0) || this.allSoldOut(product);
  }

  private getQuantity(product: Item): number {
    let quantity = 0;
    for (const v of product.variations) {
      if (v.quantity_remaining === null) {
        return Number.MAX_VALUE;
      } else if (v.quantity_remaining > 0) {
        quantity += v.quantity_remaining;
      }
    }
    return quantity;
  }

  private allSoldOut(product: Item): boolean {
    return product.variations
      .filter(v => !v.sold_out || (v.track_inventory && v.quantity_remaining > 0))
      .length === 0;
  }

  private isProductSold(product: Item): boolean {
    const available: Variation[] = [];
    for (const v of product.variations) {
      if (v.track_inventory) {
        if ((v.quantity_remaining ?? 0) > 0) {
          available.push(v);
        }
      } else {
        return false;
      }
    }
    return available.length === 0;
  }



  public getProductsInventory(locationId: string, variationIds: string[], merchantId?: string): Observable<any> {
    let apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    if (merchantId) {
      apiURL = `${environment.apiUrl}merchants/${merchantId}`;
    }
    const body = {variation_ids: variationIds};
    return this.http.post(`${apiURL}/locations/${locationId}/catalog/inventory`, body)
      .pipe(map((items: any) => {
        return items;
      }));
  }

  private getLocationById(locationId: string): Observable<object> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}venues/${locationId}`);
  }

  public getLocationsByCategories(merchantId?: string): Observable<any> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/catalog/categories`);
  }

  public getProductsByReward(rewardId: string, locationId: string, merchantId: string): Observable<any> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/loyalty/rewards/${rewardId}/locations/${locationId}/items`);
  }

  public checkQROrderOnlyOrder(restaurant: Restaurant): void {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const queryParams = { ...urlTree?.queryParams };
    if (restaurant?.qrOrderOnly && !queryParams['sharedOrderId'] && !queryParams['sharedOwnerId']) {
      if (queryParams['qrCode'] !== undefined) {
        delete queryParams['qrCode'];
        this.router.navigate([], {
          queryParams: queryParams,
          replaceUrl: true,
        });
      } else {
        this.openQrOrderModal(restaurant);
      }
    }
  }

  public get readOnlyMenu(): boolean {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const queryParams = { ...urlTree?.queryParams };
    return queryParams['readOnly'] === 'true';
  }

  private openQrOrderModal(restaurant: Restaurant): void {
    const isMobile = this.breakpointObserver.isMatched(Breakpoints.Handset);
    // this.dialog.open(OrderOnlyModalComponent, {
    //   data: {
    //     restaurant
    //   },
    //   height: isMobile ? '40vh' : '500px',
    //   width: isMobile ? '100vw' : '600px',
    //   disableClose: true
    // });
  }

  private getDailyAvailability(data: DailyAvailabilityResponse[]): DailyAvailability[] {
    if (!data) {
      return null;
    }
    const dailyAvailability: DailyAvailability[] = [];
    for (const day of data) {
      // dailyAvailability.push(new DailyAvailability(day.enabled, day.value, this.convertTimeRangeToDate(day.start_time, true), this.convertTimeRangeToDate(day.end_time, false)));
    }
    return dailyAvailability;
  }

  private convertTimeRangeToDate(range: WorkTime, isStartTime: boolean): Date {
    const date = new Date();
    if (!range) {
      if (isStartTime) {
        date.setHours(0);
        date.setMinutes(0);
      }
      if (!isStartTime) {
        date.setHours(23);
        date.setMinutes(59);
      }
    } else {
      const meridian = range.meridian ? 0 : 12;
      const hour = range.time.hour === 12 ? 0 : range.time.hour;
      date.setMinutes(range.time.minute);
      date.setHours(hour + meridian);
    }
    return date;
  }

  private faker(length): string {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }
}
